import { isInteractiveDialog } from "@choose/ui";
import { Button } from "@choose/ui/button";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@choose/ui/empty";
import { Kbd, KbdGroup } from "@choose/ui/kbd";
import { Separator } from "@choose/ui/separator";
import { Spinner } from "@choose/ui/spinner";
import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { CandyOffIcon, ServerCrashIcon } from "lucide-react";
import { useCallback, useEffect } from "react";
import { useHotkeys } from "react-hotkeys-hook";
import { useTranslation } from "react-i18next";

import { Composer } from "~/components/common/ticket/composer";
import {
  ADD_NOTE_SHORTCUT,
  ComposerNote,
  DISMISS_NOTE_SHORTCUT,
} from "~/components/common/ticket/composer-note";
import { ComposerStatusDetail } from "~/components/common/ticket/composer-status-detail";
import { TicketChannel } from "~/components/common/ticket/ticket-channel";
import { Timeline } from "~/components/common/ticket/timeline/timeline";
import { TicketDetailSidebar } from "~/components/layout/sidebars/ticket-detail-sidebar";
import { NEXT_IN_QUEUE_SHORTCUT } from "~/components/layout/sidebars/ticket-navigation-sidebar";
import { SidebarTrigger } from "~/components/ui/sidebar";
import type {
  SupportTicketsSortInput,
  TicketUpdatedSubscription,
} from "~/graphql/graphql.gen";
import {
  SupportTicketAiSuggestionStatus,
  SupportTicketsSortableDirection,
  SupportTicketsSortableFields,
  SupportTicketSubStatus,
  TicketUpdatedDocument,
  useCurrentUserQuery,
  useInfiniteTicketActivitiesQuery,
  useSetSubStatusMutation,
  useTicketQuery,
} from "~/graphql/graphql.gen";
import { useGraphQLSubscription } from "~/hooks/use-graphql-subscription";
import { track } from "~/libs/analytics";
import { TICKET_DETAIL_SIDEBAR_NAME } from "~/libs/string";
import { useComposerNoteStore } from "~/stores/composer-note-store";
import { useQueuedTicketsNextItem } from "~/stores/queue-store";
import {
  AGENT_OR_EMERCH_OR_MARKETING_ROLES,
  AGENT_ROLES,
  VIEWER_ROLES,
} from "~/stores/roles-store";

export const INVESTIGATE_SHORTCUT = "i>i";
export const WAIT_FOR_CONTACT_SHORTCUT = "w>w";
export const WAIT_FOR_SUPPLIER_SHORTCUT = "m>m";
export const RESOLVE_SHORTCUT = "e>e";

export const AI_SUGGESTIONS_COUNT = 1;
export const CONTACT_ORDERS_COUNT = 5;
export const CONTACT_TICKETS_COUNT = 5;
export const CONTACT_TICKETS_SORT_FIELD: SupportTicketsSortInput = {
  direction: SupportTicketsSortableDirection.Desc,
  field: SupportTicketsSortableFields.StatusUpdatedAt,
};
export const SCHEDULED_ACTIONS_COUNT = 50;

export const Route = createFileRoute(
  "/_auth/_app_shell/_ticket-navigation/ticket/$ticketId",
)({
  component: RouteComponent,
});

function RouteComponent() {
  const { t } = useTranslation();
  const { ticketId } = Route.useParams();

  const {
    error,
    data: ticket,
    isLoading,
  } = useTicketQuery(
    {
      ticketId: ticketId,
      aiSuggestionsFilters: {
        status: {
          in: [SupportTicketAiSuggestionStatus.Pending],
        },
      },
      aiSuggestionsFirst: AI_SUGGESTIONS_COUNT,
      contactOrdersLimit: CONTACT_ORDERS_COUNT,
      contactTicketsFirst: CONTACT_TICKETS_COUNT,
      contactTicketsSortBy: CONTACT_TICKETS_SORT_FIELD,
      scheduleActionsFirst: SCHEDULED_ACTIONS_COUNT,
    },
    {
      select: (data) => data.supportTicket,
    },
  );
  const { data: user } = useCurrentUserQuery(undefined, {
    select: (data) => data.currentUser,
  });

  const nextInQueue = useQueuedTicketsNextItem(ticketId);
  const { isOpen: isNoteOpen, setIsOpen: setIsNoteOpen } =
    useComposerNoteStore();
  const navigate = useNavigate();
  const { mutateAsync: setSubStatusAsync } = useSetSubStatusMutation();
  const queryClient = useQueryClient();
  const client = useGraphQLSubscription();

  useEffect(() => {
    const unsubscribe = client.subscribe(
      {
        query: TicketUpdatedDocument,
        variables: {},
        operationName: "ticketUpdated",
      },
      {
        next: (value: { data: TicketUpdatedSubscription }) => {
          if (value.data.supportTicketUpdated.id !== ticketId) return;

          void queryClient.invalidateQueries({
            queryKey: useTicketQuery.getKey({
              ticketId,
            }),
          });
          void queryClient.invalidateQueries({
            queryKey: useInfiniteTicketActivitiesQuery.getKey({
              ticketId: ticketId,
            }),
          });
        },
        error: () => {
          // Handle error if necessary
        },
        complete: () => {
          // Handle completion if necessary
        },
      },
    );
    return () => unsubscribe();
  }, [client, queryClient, ticketId]);

  useEffect(() => {
    setIsNoteOpen(false);
  }, [ticketId, setIsNoteOpen]);

  const changeSubStatus = useCallback(
    async (subStatus: SupportTicketSubStatus) => {
      await setSubStatusAsync({
        ticketId: ticketId,
        subStatus: subStatus,
      });

      void queryClient.invalidateQueries({
        queryKey: useTicketQuery.getKey({
          ticketId,
        }),
      });
      void queryClient.invalidateQueries({
        queryKey: useInfiniteTicketActivitiesQuery.getKey({
          ticketId,
        }),
      });
    },
    [queryClient, setSubStatusAsync, ticketId],
  );

  const changeSubStatusAndGoNext = useCallback(
    (subStatus: SupportTicketSubStatus) => {
      const nextTicketId = nextInQueue?.id;
      void changeSubStatus(subStatus);
      if (nextTicketId) {
        void navigate({
          to: "/ticket/$ticketId",
          params: {
            ticketId: nextTicketId,
          },
        });
      }
    },
    [changeSubStatus, navigate, nextInQueue],
  );

  const hasViewerRights =
    user?.__typename === "ActorInternalUser" &&
    VIEWER_ROLES.includes(user.internalUser.supportRole);
  const hasAgentRights =
    user?.__typename === "ActorInternalUser" &&
    AGENT_ROLES.includes(user.internalUser.supportRole);
  const hasAgentOrEmerchOrMarketingRights =
    user?.__typename === "ActorInternalUser" &&
    AGENT_OR_EMERCH_OR_MARKETING_ROLES.includes(user.internalUser.supportRole);

  useHotkeys(
    INVESTIGATE_SHORTCUT,
    (e) => {
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      e.preventDefault();

      void changeSubStatus(SupportTicketSubStatus.Investigating);
      track("ChangeStatusInvestigatingShortcut");
    },
    {
      enabled: hasAgentRights,
      ignoreEventWhen: (e) => isInteractiveDialog(e),
      useKey: true,
    },
  );
  useHotkeys(
    WAIT_FOR_CONTACT_SHORTCUT,
    (e) => {
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      e.preventDefault();

      void changeSubStatus(SupportTicketSubStatus.WaitingContact);
      track("ChangeStatusWaitingContactShortcut");
    },
    {
      enabled: hasAgentRights,
      ignoreEventWhen: (e) => isInteractiveDialog(e),
      useKey: true,
    },
  );
  useHotkeys(
    WAIT_FOR_SUPPLIER_SHORTCUT,
    (e) => {
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      e.preventDefault();

      void changeSubStatus(SupportTicketSubStatus.WaitingSupplier);
      track("ChangeStatusWaitingSupplierShortcut");
    },
    {
      enabled: hasAgentRights,
      ignoreEventWhen: (e) => isInteractiveDialog(e),
      useKey: true,
    },
  );
  useHotkeys(
    RESOLVE_SHORTCUT,
    (e) => {
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      e.preventDefault();

      void changeSubStatusAndGoNext(SupportTicketSubStatus.Resolved);
      track("ChangeStatusResolvedShortcut");
    },
    {
      enabled: hasAgentRights,
      ignoreEventWhen: (e) => isInteractiveDialog(e),
      useKey: true,
    },
  );

  if (isLoading) {
    return (
      <>
        <title>{t("app_name")}</title>
        <main className="h-svh w-full overflow-hidden md:p-2">
          <div className="bg-container flex size-full overflow-hidden md:rounded-md md:border">
            <div className="bg-background flex size-full flex-col items-center justify-center">
              <Empty className="min-h-[300px] w-full max-w-md">
                <EmptyHeader>
                  <EmptyMedia variant="icon">
                    <Spinner className="size-4" />
                  </EmptyMedia>
                  <EmptyTitle>{t("ticket.loading.title")}</EmptyTitle>
                  <EmptyDescription>
                    {t("ticket.loading.description")}
                  </EmptyDescription>
                </EmptyHeader>
              </Empty>
            </div>
          </div>
        </main>
      </>
    );
  }

  if (!ticket || (error as Error | undefined)?.message === "ticket_not_found")
    return (
      <>
        <title>{`${t("ticket.not_found.title")} - ${t("app_name")}`}</title>
        <main className="h-svh w-full overflow-hidden md:p-2">
          <div className="bg-container flex size-full overflow-hidden md:rounded-md md:border">
            <div className="bg-background flex size-full flex-col justify-center">
              <Empty>
                <EmptyHeader>
                  <EmptyMedia variant="icon">
                    <CandyOffIcon />
                  </EmptyMedia>
                  <EmptyTitle>{t("ticket.not_found.title")}</EmptyTitle>
                  <EmptyDescription>
                    {t("ticket.not_found.description")}
                  </EmptyDescription>
                </EmptyHeader>
              </Empty>
            </div>
          </div>
        </main>
      </>
    );

  if (error) {
    return (
      <>
        <title>{t("app_name")}</title>
        <main className="h-svh w-full overflow-hidden md:p-2">
          <div className="bg-container flex size-full overflow-hidden md:rounded-md md:border">
            <div className="bg-background flex size-full flex-col justify-center">
              <Empty>
                <EmptyHeader>
                  <EmptyMedia variant="icon">
                    <ServerCrashIcon />
                  </EmptyMedia>
                  <EmptyTitle>{t("ticket.error")}</EmptyTitle>
                </EmptyHeader>
              </Empty>
            </div>
          </div>
        </main>
      </>
    );
  }

  return (
    <>
      <title>{`${ticket.title} - ${t("app_name")}`}</title>
      <main className="h-svh w-full overflow-hidden md:p-2">
        <div className="bg-container flex h-full w-full overflow-hidden md:rounded-md md:border">
          <div className="bg-background flex h-full w-full flex-col">
            <header className="flex h-10 w-full min-w-0 shrink-0 items-center gap-2 border-b px-4">
              <span className="items-center truncate">{ticket.title}</span>
              <Separator orientation="vertical" className="my-3 ml-1" />
              <TicketChannel channel={ticket.channel} />
              <Separator
                orientation="vertical"
                className="my-3 ml-2 flex md:hidden"
              />
              <SidebarTrigger
                name={TICKET_DETAIL_SIDEBAR_NAME}
                className="-mr-1 inline-flex md:hidden"
              />
            </header>
            <div className="relative flex max-h-full min-h-0 grow flex-col">
              <div className="flex max-h-full min-h-0 grow flex-col overflow-y-auto">
                <div className="relative grow overflow-hidden">
                  <Timeline
                    isReadByContact={ticket.isReadByContact}
                    ticket={ticket}
                  />
                </div>

                <footer
                  className="relative shrink-0 p-4"
                  onSubmit={(e) => e.preventDefault()}
                >
                  <ComposerStatusDetail ticket={ticket} />
                  <Composer disabled={!hasAgentRights} ticket={ticket} />
                  <ComposerNote
                    disabled={!hasViewerRights}
                    ticketId={ticket.id}
                  />
                </footer>
              </div>
              <div className="flex items-center border-t p-2">
                {isNoteOpen ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setIsNoteOpen(false)}
                  >
                    {t("ticket.actions.add_note")}
                    <KbdGroup>
                      <Kbd>
                        {DISMISS_NOTE_SHORTCUT.toLowerCase().slice(0, 3)}
                      </Kbd>
                    </KbdGroup>
                  </Button>
                ) : (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setIsNoteOpen(true)}
                    disabled={!hasAgentOrEmerchOrMarketingRights}
                  >
                    {t("ticket.actions.add_note")}
                    <KbdGroup>
                      <Kbd>{ADD_NOTE_SHORTCUT.toUpperCase()}</Kbd>
                    </KbdGroup>
                  </Button>
                )}
                <div className="ml-auto hidden sm:block">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={!nextInQueue}
                    onClick={() => {
                      if (!nextInQueue) return;
                      void navigate({
                        to: "/ticket/$ticketId",
                        params: {
                          ticketId: nextInQueue.id,
                        },
                      });
                    }}
                  >
                    {t("ticket.actions.next")}
                    <KbdGroup>
                      <Kbd>{NEXT_IN_QUEUE_SHORTCUT.toUpperCase()}</Kbd>
                    </KbdGroup>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
      <TicketDetailSidebar
        name="ticket-detail-sidebar"
        side="right"
        ticket={ticket}
        style={
          {
            "--sidebar-width": "365px",
          } as React.CSSProperties
        }
      />
    </>
  );
}
